import * as z from 'zod';

export const SearchRequest = (() => {
  let wixSearchV1FacetClauseHierarchicalFacetSchema: z.ZodType<any> =
    z.intersection(
      z.object({
        nestedAggregation: z
          .lazy(() => wixSearchV1FacetClauseHierarchicalFacetSchema)
          .optional(),
      }),
      z.xor([
        z.object({
          term: z.never().optional(),
          aggregation: z.never().optional(),
        }),
        z.object({
          aggregation: z.never().optional(),
          term: z.object({
            name: z
              .string()
              .describe('The name of the faceted attribute.')
              .optional(),
            limit: z
              .number()
              .int()
              .describe(
                'Limit the number of facet values returned. Default is 10.'
              )
              .optional()
              .nullable(),
          }),
        }),
        z.object({
          term: z.never().optional(),
          aggregation: z.object({
            name: z
              .string()
              .describe('The name of the faceted attribute.')
              .optional(),
            aggregation: z
              .enum(['MIN', 'MAX', 'SUM'])
              .describe('Aggregation type.')
              .optional(),
          }),
        }),
      ])
    );
  return z.object({
    options: z
      .object({
        query: z
          .string()
          .describe(
            'Text to search for. All searchable fields will be searched.'
          )
          .min(0)
          .max(100)
          .optional()
          .nullable(),
        documentType: z
          .string()
          .describe(
            'Document type of documents to search for. All document types are searched if not provided.'
          )
          .min(2)
          .optional()
          .nullable(),
        ordering: z
          .object({
            ordering: z
              .array(
                z.object({
                  fieldName: z.string().min(2).optional().nullable(),
                  direction: z
                    .enum(['UninitializedDirection', 'ASC', 'DESC'])
                    .optional(),
                })
              )
              .optional(),
          })
          .describe('Fields to order by.')
          .optional(),
        paging: z
          .object({
            skip: z
              .number()
              .int()
              .describe('Number of items to skip in the result set.')
              .min(0)
              .max(100000)
              .optional(),
            limit: z
              .number()
              .int()
              .describe('Number of items to fetch (effectively page size).')
              .min(0)
              .max(1000)
              .optional(),
            offset: z
              .number()
              .int()
              .describe('Number of items to skip in the result set.')
              .min(0)
              .max(100000)
              .optional(),
          })
          .describe('Paging parameters.')
          .optional(),
        language: z
          .string()
          .describe('Language to search in.')
          .min(2)
          .optional()
          .nullable(),
        filter: z
          .record(z.string(), z.any())
          .describe(
            "Filter in platformized query language (for example {'field': {'$eq': 'value'}})"
          )
          .optional()
          .nullable(),
        facets: z
          .object({
            clauses: z
              .array(
                z.intersection(
                  z.object({}),
                  z.xor([
                    z.object({
                      term: z.never().optional(),
                      aggregation: z.never().optional(),
                      hierarchical: z.never().optional(),
                    }),
                    z.object({
                      aggregation: z.never().optional(),
                      hierarchical: z.never().optional(),
                      term: z.object({
                        name: z
                          .string()
                          .describe('The name of the faceted attribute.')
                          .optional(),
                        limit: z
                          .number()
                          .int()
                          .describe(
                            'Limit the number of facet values returned. Default is 10.'
                          )
                          .optional()
                          .nullable(),
                      }),
                    }),
                    z.object({
                      term: z.never().optional(),
                      hierarchical: z.never().optional(),
                      aggregation: z.object({
                        name: z
                          .string()
                          .describe('The name of the faceted attribute.')
                          .optional(),
                        aggregation: z
                          .enum(['MIN', 'MAX', 'SUM'])
                          .describe('Aggregation type.')
                          .optional(),
                      }),
                    }),
                    z.object({
                      term: z.never().optional(),
                      aggregation: z.never().optional(),
                      hierarchical:
                        wixSearchV1FacetClauseHierarchicalFacetSchema,
                    }),
                  ])
                )
              )
              .optional(),
          })
          .describe('The facets to retrieve.')
          .optional(),
        fuzzy: z
          .boolean()
          .describe(
            "Enable fuzzy search (eg. query 'kalvin clein' will match document with 'calvin klein' in title)."
          )
          .optional()
          .nullable(),
        highlight: z
          .boolean()
          .describe(
            'Highlight texts matching the query. Highlighted text will be wrapped with <mark/> tag. Defaults to true if not provided.'
          )
          .optional()
          .nullable(),
        searchFields: z.array(z.string()).optional(),
        fields: z.array(z.string()).optional(),
        properties: z
          .array(
            z.object({
              name: z.string().optional(),
              value: z.any().optional().nullable(),
            })
          )
          .max(10)
          .optional(),
        includeSeoHidden: z
          .boolean()
          .describe(
            'Include seo hidden documents. Defaults to false if not provided.'
          )
          .optional()
          .nullable(),
      })
      .optional(),
  });
})();
export const SearchResponse = (() => {
  let wixSearchV1FacetsResponseSchema: z.ZodType<any> = z.intersection(
    z.object({}),
    z.xor([
      z.object({
        terms: z.never().optional(),
        minAggregation: z.never().optional(),
        maxAggregation: z.never().optional(),
        minMaxAggregation: z.never().optional(),
        hierarchicalAggregation: z.never().optional(),
        sumAggregation: z.never().optional(),
      }),
      z.object({
        minAggregation: z.never().optional(),
        maxAggregation: z.never().optional(),
        minMaxAggregation: z.never().optional(),
        hierarchicalAggregation: z.never().optional(),
        sumAggregation: z.never().optional(),
        terms: z.object({
          facet: z
            .string()
            .describe('Facet field (for example productCategory)')
            .optional(),
          facets: z
            .array(
              z.object({
                facetValue: z
                  .string()
                  .describe('Facet field value (for example "Shoes", "Socks")')
                  .optional(),
                count: z
                  .number()
                  .int()
                  .describe('Document count within the group')
                  .optional(),
              })
            )
            .optional(),
        }),
      }),
      z.object({
        terms: z.never().optional(),
        maxAggregation: z.never().optional(),
        minMaxAggregation: z.never().optional(),
        hierarchicalAggregation: z.never().optional(),
        sumAggregation: z.never().optional(),
        minAggregation: z.object({
          facet: z
            .string()
            .describe('Facet field (for example productPrice)')
            .optional(),
          minValue: z
            .number()
            .describe('The minimum value across all documents')
            .optional()
            .nullable(),
        }),
      }),
      z.object({
        terms: z.never().optional(),
        minAggregation: z.never().optional(),
        minMaxAggregation: z.never().optional(),
        hierarchicalAggregation: z.never().optional(),
        sumAggregation: z.never().optional(),
        maxAggregation: z.object({
          facet: z
            .string()
            .describe('Facet field (for example productPrice)')
            .optional(),
          maxValue: z
            .number()
            .describe('The maximum value across all documents')
            .optional()
            .nullable(),
        }),
      }),
      z.object({
        terms: z.never().optional(),
        minAggregation: z.never().optional(),
        maxAggregation: z.never().optional(),
        hierarchicalAggregation: z.never().optional(),
        sumAggregation: z.never().optional(),
        minMaxAggregation: z.object({
          facet: z
            .string()
            .describe('Facet field (for example productPrice)')
            .optional(),
          minValue: z
            .number()
            .describe('The minimum value across all documents')
            .optional()
            .nullable(),
          maxValue: z
            .number()
            .describe('The maximum value across all documents')
            .optional()
            .nullable(),
        }),
      }),
      z.object({
        terms: z.never().optional(),
        minAggregation: z.never().optional(),
        maxAggregation: z.never().optional(),
        minMaxAggregation: z.never().optional(),
        sumAggregation: z.never().optional(),
        hierarchicalAggregation: z.object({
          facet: z.string().optional(),
          values: z
            .array(
              z.object({
                value: z.string().optional(),
                facets: z
                  .lazy(() => wixSearchV1FacetsResponseSchema)
                  .optional(),
                count: z.number().int().optional(),
              })
            )
            .optional(),
        }),
      }),
      z.object({
        terms: z.never().optional(),
        minAggregation: z.never().optional(),
        maxAggregation: z.never().optional(),
        minMaxAggregation: z.never().optional(),
        hierarchicalAggregation: z.never().optional(),
        sumAggregation: z.object({
          facet: z
            .string()
            .describe('Facet field (for example productPrice)')
            .optional(),
          value: z
            .number()
            .describe('The sum value across all documents')
            .optional()
            .nullable(),
        }),
      }),
    ])
  );
  return z.object({
    documents: z.array(z.record(z.string(), z.any())).optional(),
    nextPage: z
      .object({
        total: z
          .number()
          .int()
          .describe('Total number of items across all pages')
          .optional(),
        skip: z
          .number()
          .int()
          .describe('The number of items to skip')
          .optional(),
        limit: z
          .number()
          .int()
          .describe('The number of items to retrieve in one page')
          .optional(),
      })
      .optional(),
    facets: z.array(wixSearchV1FacetsResponseSchema).optional(),
  });
})();
export const FederatedSearchRequest = z.object({
  options: z
    .object({
      query: z
        .string()
        .describe('Query phrase to use.')
        .min(0)
        .max(100)
        .optional()
        .nullable(),
      language: z
        .string()
        .describe('Language to search in.')
        .min(2)
        .optional()
        .nullable(),
      limit: z
        .number()
        .int()
        .describe('Limit of documents to return per document type.')
        .min(1)
        .max(100)
        .optional()
        .nullable(),
      fuzzy: z
        .boolean()
        .describe(
          "Enable fuzzy search (for example query 'kalvin clein' will match document with 'calvin klein' in title)."
        )
        .optional()
        .nullable(),
      highlight: z
        .boolean()
        .describe(
          'Highlight texts matching the query. Highlighted text will be wrapped with <mark/> tag. Defaults to true if not provided.'
        )
        .optional()
        .nullable(),
      searchFields: z.array(z.string()).optional(),
      documentTypes: z.array(z.string()).optional(),
      includeSeoHidden: z
        .boolean()
        .describe(
          'Include seo hidden documents. Defaults to false if not provided.'
        )
        .optional()
        .nullable(),
      properties: z
        .array(
          z.object({
            name: z.string().optional(),
            value: z.any().optional().nullable(),
          })
        )
        .max(10)
        .optional(),
    })
    .optional(),
});
export const FederatedSearchResponse = z.object({
  results: z
    .array(
      z.object({
        documentType: z
          .string()
          .describe('Document type of documents')
          .optional()
          .nullable(),
        documents: z.array(z.record(z.string(), z.any())).optional(),
        total: z
          .number()
          .int()
          .describe('Total count of matching documents for document type')
          .optional(),
      })
    )
    .optional(),
});
export const SuggestRequest = z.object({
  options: z
    .object({
      query: z
        .string()
        .describe(
          'Text to search for. Fields configured in suggester configuration will be searched.'
        )
        .min(0)
        .max(50)
        .optional()
        .nullable(),
      documentType: z
        .string()
        .describe(
          'Document type of documents to search for. All document types are searched if not provided.'
        )
        .min(2)
        .optional()
        .nullable(),
      ordering: z
        .object({
          ordering: z
            .array(
              z.object({
                fieldName: z.string().min(2).optional().nullable(),
                direction: z
                  .enum(['UninitializedDirection', 'ASC', 'DESC'])
                  .optional(),
              })
            )
            .optional(),
        })
        .describe('Fields to order by.')
        .optional(),
      limit: z
        .number()
        .int()
        .describe('Number of suggested document to return.')
        .min(1)
        .max(100)
        .optional(),
      language: z
        .string()
        .describe('Language to search in.')
        .min(2)
        .optional()
        .nullable(),
      filter: z
        .record(z.string(), z.any())
        .describe(
          "Filter in platformized query language (for example {'field': {'$eq': 'value'}})"
        )
        .optional()
        .nullable(),
      searchFields: z.array(z.string()).optional(),
      fields: z.array(z.string()).optional(),
      includeSeoHidden: z
        .boolean()
        .describe(
          'Include seo hidden documents. Defaults to false if not provided.'
        )
        .optional()
        .nullable(),
      properties: z
        .array(
          z.object({
            name: z.string().optional(),
            value: z.any().optional().nullable(),
          })
        )
        .max(10)
        .optional(),
    })
    .optional(),
});
export const SuggestResponse = z.object({
  documents: z.array(z.record(z.string(), z.any())).optional(),
});
export const FederatedSuggestRequest = z.object({
  options: z
    .object({
      query: z
        .string()
        .describe(
          'Text to search for. Fields configured in suggester configuration will be searched.'
        )
        .min(0)
        .max(50)
        .optional()
        .nullable(),
      language: z
        .string()
        .describe('Language to search in.')
        .min(2)
        .optional()
        .nullable(),
      limit: z
        .number()
        .int()
        .describe('Number of suggested document to return per document type.')
        .min(1)
        .max(100)
        .optional(),
      searchFields: z.array(z.string()).optional(),
      documentTypes: z.array(z.string()).optional(),
      includeSeoHidden: z
        .boolean()
        .describe(
          'Include seo hidden documents. Defaults to false if not provided.'
        )
        .optional()
        .nullable(),
      properties: z
        .array(
          z.object({
            name: z.string().optional(),
            value: z.any().optional().nullable(),
          })
        )
        .max(10)
        .optional(),
    })
    .optional(),
});
export const FederatedSuggestResponse = z.object({
  results: z
    .array(
      z.object({
        documentType: z
          .string()
          .describe('Document type of documents')
          .optional()
          .nullable(),
        documents: z.array(z.record(z.string(), z.any())).optional(),
      })
    )
    .optional(),
});
export const RelatedRequest = z.object({
  options: z
    .object({
      documentId: z
        .string()
        .describe('ID of the document to fetch related documents for.')
        .min(1)
        .optional()
        .nullable(),
      documentType: z
        .string()
        .describe('Document type of the document.')
        .min(2)
        .optional()
        .nullable(),
      ordering: z
        .object({
          ordering: z
            .array(
              z.object({
                fieldName: z.string().min(2).optional().nullable(),
                direction: z
                  .enum(['UninitializedDirection', 'ASC', 'DESC'])
                  .optional(),
              })
            )
            .optional(),
        })
        .describe('Fields to order by.')
        .optional(),
      language: z
        .string()
        .describe('Language to search in.')
        .min(2)
        .optional()
        .nullable(),
      filter: z
        .record(z.string(), z.any())
        .describe(
          "Filter in platformized query language (for example {'field': {'$eq': 'value'}})."
        )
        .optional()
        .nullable(),
      searchFields: z.array(z.string()).optional(),
      fields: z.array(z.string()).optional(),
      limit: z
        .number()
        .int()
        .describe('Number of related documents to return')
        .optional(),
      properties: z
        .array(
          z.object({
            name: z.string().optional(),
            value: z.any().optional().nullable(),
          })
        )
        .max(10)
        .optional(),
      includeSeoHidden: z
        .boolean()
        .describe(
          'Include seo hidden documents. Defaults to false if not provided.'
        )
        .optional()
        .nullable(),
    })
    .optional(),
});
export const RelatedResponse = z.object({
  documents: z.array(z.record(z.string(), z.any())).optional(),
});
export const AutocompleteRequest = z.object({
  options: z
    .object({
      query: z
        .string()
        .describe('Query phrase to fetch completions for.')
        .min(0)
        .max(50)
        .optional()
        .nullable(),
      documentType: z
        .string()
        .describe('Document type to use to search for phrases.')
        .min(2)
        .optional()
        .nullable(),
      limit: z
        .number()
        .int()
        .describe('Limit of phrases to fetch.')
        .min(1)
        .max(100)
        .optional(),
      language: z
        .string()
        .describe('Language to search in.')
        .min(2)
        .optional()
        .nullable(),
      filter: z
        .record(z.string(), z.any())
        .describe(
          "Filter in platfromized query language (for example {'field': {'$eq': 'value'}})"
        )
        .optional()
        .nullable(),
      searchFields: z.array(z.string()).optional(),
      includeSeoHidden: z
        .boolean()
        .describe(
          'Include seo hidden documents. Defaults to false if not provided.'
        )
        .optional()
        .nullable(),
    })
    .optional(),
});
export const AutocompleteResponse = z.object({
  values: z
    .array(
      z.object({ query: z.string().describe('Suggested phrase.').optional() })
    )
    .optional(),
});
export const FederatedAutocompleteRequest = z.object({
  options: z
    .object({
      query: z
        .string()
        .describe('Query phrase to fetch completions for.')
        .min(0)
        .max(50)
        .optional()
        .nullable(),
      language: z
        .string()
        .describe('Language to search in.')
        .min(2)
        .optional()
        .nullable(),
      limit: z
        .number()
        .int()
        .describe('Number of queries to return per document type.')
        .min(1)
        .max(100)
        .optional(),
      searchFields: z.array(z.string()).optional(),
      documentTypes: z.array(z.string()).optional(),
      includeSeoHidden: z
        .boolean()
        .describe(
          'Include seo hidden documents. Defaults to false if not provided.'
        )
        .optional()
        .nullable(),
    })
    .optional(),
});
export const FederatedAutocompleteResponse = z.object({
  results: z
    .array(
      z.object({
        documentType: z
          .string()
          .describe('Document type of queries')
          .optional()
          .nullable(),
        values: z
          .array(
            z.object({
              query: z.string().describe('Suggested phrase.').optional(),
            })
          )
          .optional(),
      })
    )
    .optional(),
});
export const TrendingRequest = z.object({
  options: z
    .object({
      documentTypes: z.array(z.string()).optional(),
      language: z.string().min(2).optional().nullable(),
      includeSeoHidden: z
        .boolean()
        .describe(
          'Include seo hidden documents. Defaults to false if not provided.'
        )
        .optional()
        .nullable(),
      properties: z
        .array(
          z.object({
            name: z.string().optional(),
            value: z.any().optional().nullable(),
          })
        )
        .max(10)
        .optional(),
    })
    .optional(),
});
export const TrendingResponse = z.object({
  results: z
    .array(
      z.object({
        documentType: z.string().optional(),
        documents: z.array(z.record(z.string(), z.any())).optional(),
      })
    )
    .optional(),
});
